/**
 * Budget Table Phase Header Component
 *
 * Colored, collapsible header row for budget phases in table view
 * Displays phase name, totals, and controls for expansion and adding items
 *
 * Design specs from Figma:
 * - Height: 60px
 * - Padding: 20px horizontal
 * - Font: 16px bold white
 * - Colors: purple (#7846FF), teal (#13D480), orange (#F1745B)
 * - Columns: checkbox (50px), name (flex-1), 3x totals (160px each), chevron, "+ Adicionar"
 */

import { cn } from "@/lib/class-control";
import { Checkbox } from "@/components/ui/checkbox";
import { PhaseColor } from "@/types/orcamentos.interface";

interface BudgetTablePhaseHeaderProps {
  /** Phase name to display */
  phaseName: string;
  /** Hierarchical number (e.g., "1", "2") */
  phaseNumber: string;
  /** Color identifier for background */
  phaseColor: PhaseColor;
  /** Whether phase is expanded */
  isExpanded: boolean;
  /** Toggle expansion callback */
  onToggle: () => void;
  /** Total executed budget */
  totalExecutado: number;
  /** Total approved budget */
  totalAprovado: number;
  /** Checkbox checked state */
  checked: boolean;
  /** Checkbox change callback */
  onCheckChange: (checked: boolean) => void;
}

/**
 * Get background color based on phase color identifier
 */
function getPhaseBackground(color: PhaseColor): string {
  const colorMap: Record<PhaseColor, string> = {
    purple: "#7846FF",
    teal: "#13D480",
    orange: "#FF6B4A",
  };
  return colorMap[color];
}

/**
 * Format currency to Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function BudgetTablePhaseHeader({
  phaseName,
  phaseNumber,
  phaseColor,
  isExpanded,
  onToggle,
  totalExecutado,
  totalAprovado,
  checked,
  onCheckChange,
}: BudgetTablePhaseHeaderProps) {
  return (
    <tr
      className="h-[60px] cursor-pointer"
      style={{ backgroundColor: getPhaseBackground(phaseColor) }}
      onClick={onToggle}
    >
      {/* Checkbox column */}
      <td className="w-[50px] text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <Checkbox
            checked={checked}
            onCheckedChange={onCheckChange}
            className="border-white data-[state=checked]:bg-white data-[state=checked]:text-[#7846FF]"
          />
        </div>
      </td>

      {/* Phase name column with hierarchical number */}
      <td className="px-[20px] text-[16px] font-bold text-white">
        <span className="mr-2">{phaseNumber}</span>
        {phaseName}
      </td>

      {/* Empty column for Última atualização */}
      <td className="w-[160px]" />

      {/* Total Orçamento Aprovado */}
      <td className="w-[180px] px-[16px] text-right text-[14px] font-semibold text-white">
        {formatCurrency(totalAprovado)}
      </td>

      {/* Total Orçamento Executado */}
      <td className="w-[180px] px-[16px] text-right text-[14px] font-semibold text-white">
        {formatCurrency(totalExecutado)}
      </td>

      {/* Empty column for Percentual */}
      <td className="w-[100px]" />

      {/* Empty column for Actions */}
      <td className="w-[100px] px-[16px]"></td>
    </tr>
  );
}
